import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Search, ExternalLink, Filter, X, LayoutGrid, List, ChevronDown, ChevronRight, Share2, Check, Users, Link2, Copy, Loader2, Plus, Edit, Trash2, ArrowUpDown, Shuffle, CheckSquare, Square, Layers, Table as TableIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import MultiSelectTag from "@/components/MultiSelectTag";

import axios from "axios";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

const FilterDropdown = ({ label, options, selected, onSelect, onClear }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono transition-colors ${
          selected ? "bg-[#ff3737]/20 text-[#ff3737] border border-[#ff3737]/30" : "bg-white/5 text-[#ebebeb]/60 border border-white/10 hover:bg-white/10"
        }`} data-testid={`filter-${label.toLowerCase()}`}>
        {selected || label}
        {selected ? <X className="w-3 h-3 cursor-pointer" onClick={(e) => { e.stopPropagation(); onClear(); }} /> : <ChevronDown className="w-3 h-3" />}
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-30 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl max-h-64 overflow-y-auto min-w-[180px]">
          {options.map((opt) => (
            <button key={opt} onClick={() => { onSelect(opt); setOpen(false); }}
              className={`block w-full text-left px-3 py-2 text-xs font-mono transition-colors ${
                selected === opt ? "text-[#ff3737] bg-[#ff3737]/10" : "text-[#ebebeb]/70 hover:bg-white/5 hover:text-white"
              }`}>{opt}</button>
          ))}
        </div>
      )}
    </div>
  );
};

const EMPTY_FORM = { brand: "", campaign: "", version: "", demo_url: "", date: "", categories: [], formats: [], interactions: [], thumbnail_url: "" };

const SORT_OPTIONS = [
  { value: "date-desc",  label: "Newest first" },
  { value: "date-asc",   label: "Oldest first" },
  { value: "brand-asc",  label: "Brand A → Z" },
  { value: "brand-desc", label: "Brand Z → A" },
  { value: "shuffle",    label: "Shuffle" },
];

const SortDropdown = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = SORT_OPTIONS.find((o) => o.value === value) || SORT_OPTIONS[0];
  const Icon = value === "shuffle" ? Shuffle : ArrowUpDown;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-white/5 text-[#ebebeb]/70 border border-white/10 hover:bg-white/10 transition-colors"
        data-testid="gallery-sort-trigger"
      >
        <Icon className="w-3 h-3" />
        {current.label}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 z-30 bg-[#1a1a1a] border border-white/10 rounded-lg shadow-xl min-w-[160px]">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`block w-full text-left px-3 py-2 text-xs font-mono transition-colors ${
                value === opt.value ? "text-[#ff3737] bg-[#ff3737]/10" : "text-[#ebebeb]/70 hover:bg-white/5 hover:text-white"
              }`}
              data-testid={`gallery-sort-${opt.value}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const AdGallery = ({ mode = "admin", clientToken = null, publicAdUrls = null, canEdit = true }) => {
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState(null);
  const [format, setFormat] = useState(null);
  const [interaction, setInteraction] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [sortMode, setSortMode] = useState("date-desc"); // "date-desc" | "date-asc" | "brand-asc" | "brand-desc" | "shuffle"
  const [shuffleSeed, setShuffleSeed] = useState(0);
  // Grouping (brand+campaign collapses into one card). Admin-only "table" mode bypasses.
  const [groupMode, setGroupMode] = useState("grouped"); // "grouped" | "flat"
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  // Data from API
  const [items, setItems] = useState([]);
  const [options, setOptions] = useState({ categories: [], formats: [], interactions: [] });
  const [loading, setLoading] = useState(true);

  // Admin: share mode
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [sharing, setSharing] = useState(false);
  const [allShares, setAllShares] = useState([]);
  const [generatedLink, setGeneratedLink] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [playlistName, setPlaylistName] = useState("");

  // Admin: CRUD
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [thumbFile, setThumbFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Client: shared ads filter
  const [sharedUrls, setSharedUrls] = useState(null);
  const [loadingShared, setLoadingShared] = useState(mode === "client");

  const getToken = () => mode === "admin" ? localStorage.getItem("adminToken") : (clientToken || localStorage.getItem("clientToken"));

  // Fetch showcase items from API
  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, optsRes] = await Promise.all([
        axios.get(`${API}/showcase`),
        axios.get(`${API}/showcase/options`),
      ]);
      setItems(itemsRes.data || []);
      setOptions(optsRes.data || { categories: [], formats: [], interactions: [] });
    } catch {
      toast.error("Failed to load gallery");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Fetch shared ads for client mode
  useEffect(() => {
    if (mode !== "client") return;
    const token = getToken();
    if (!token) { setLoadingShared(false); return; }
    axios.get(`${API}/shared_ads`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setSharedUrls(new Set(res.data.ad_urls || res.data.shared_urls || [])))
      .catch(() => setSharedUrls(new Set()))
      .finally(() => setLoadingShared(false));
  }, [mode, clientToken]);

  // Admin: fetch shares
  const fetchShares = useCallback(() => {
    if (mode !== "admin") return;
    const token = getToken();
    if (!token) return;
    axios.get(`${API}/shared_ads`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setAllShares(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  }, [mode]);

  useEffect(() => { fetchShares(); }, [fetchShares]);

  // Filter items
  const sourceItems = useMemo(() => {
    if (mode === "public" && publicAdUrls) {
      const urlSet = new Set(publicAdUrls);
      return items.filter(item => urlSet.has(item.preview_url));
    }
    if (mode === "client" && sharedUrls) {
      return items.filter(item => sharedUrls.has(item.preview_url));
    }
    return items;
  }, [mode, items, sharedUrls, publicAdUrls]);

  const filtered = useMemo(() => {
    return sourceItems.filter((item) => {
      const searchLower = search.toLowerCase();
      if (search && !item.title?.toLowerCase().includes(searchLower) && !item.brand?.toLowerCase().includes(searchLower)) return false;
      if (category && !(item.categories || []).includes(category)) return false;
      if (format && !(item.formats || []).includes(format)) return false;
      if (interaction && !(item.interactions || []).includes(interaction)) return false;
      return true;
    });
  }, [sourceItems, search, category, format, interaction]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (sortMode === "shuffle") {
      // Deterministic shuffle keyed by shuffleSeed (so re-renders are stable)
      const rng = (n) => {
        let h = shuffleSeed + n * 2654435761;
        h = (h ^ (h >>> 16)) >>> 0;
        return (h % 10000) / 10000;
      };
      return arr
        .map((item, i) => ({ item, k: rng(i) }))
        .sort((a, b) => a.k - b.k)
        .map(({ item }) => item);
    }
    const byDate = (a, b) => (a.date || "").localeCompare(b.date || "");
    const byBrand = (a, b) => (a.brand || a.title || "").toLowerCase().localeCompare((b.brand || b.title || "").toLowerCase());
    switch (sortMode) {
      case "date-asc":   return arr.sort(byDate);
      case "date-desc":  return arr.sort((a, b) => byDate(b, a));
      case "brand-asc":  return arr.sort(byBrand);
      case "brand-desc": return arr.sort((a, b) => byBrand(b, a));
      default:           return arr;
    }
  }, [filtered, sortMode, shuffleSeed]);

  const activeFilters = [category, format, interaction].filter(Boolean).length;
  const clearAll = () => { setSearch(""); setCategory(null); setFormat(null); setInteraction(null); };

  // Group items by brand + campaign. The "rep" item is the FIRST one added per
  // group (sorted by created_at ascending), per user spec (option B).
  const groups = useMemo(() => {
    const map = new Map(); // key → { items: [...], rep: firstItem }
    for (const it of sorted) {
      const key = `${(it.brand || it.title || "").trim().toLowerCase()}::${(it.campaign || "").trim().toLowerCase()}`;
      if (!map.has(key)) map.set(key, { key, items: [], rep: null });
      map.get(key).items.push(it);
    }
    // Resolve rep = first added (oldest created_at)
    for (const g of map.values()) {
      const sortedByCreated = [...g.items].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
      g.rep = sortedByCreated[0];
    }
    // Preserve outer sort order using the rep position in `sorted`
    const seen = new Set();
    const ordered = [];
    for (const it of sorted) {
      const key = `${(it.brand || it.title || "").trim().toLowerCase()}::${(it.campaign || "").trim().toLowerCase()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      ordered.push(map.get(key));
    }
    return ordered;
  }, [sorted]);

  // Items to render in grid/list views. When grouped, each group contributes
  // its rep; if expanded, the rep is followed inline by the rest of its
  // members in the same row of the CSS grid.
  const displayItems = useMemo(() => {
    if (groupMode === "flat" || mode !== "admin" && groupMode !== "grouped") {
      // flat = show everything
      return sorted.map(it => ({ kind: "item", item: it, groupKey: null, hasGroup: false }));
    }
    if (groupMode === "flat") return sorted.map(it => ({ kind: "item", item: it, groupKey: null, hasGroup: false }));
    // grouped
    const out = [];
    for (const g of groups) {
      const isExpanded = expandedGroups.has(g.key);
      const otherMembers = g.items.filter(m => m.id !== g.rep?.id);
      out.push({
        kind: "rep",
        item: g.rep,
        groupKey: g.key,
        hasGroup: g.items.length > 1,
        memberCount: g.items.length,
        expanded: isExpanded,
      });
      if (isExpanded && otherMembers.length > 0) {
        // Insert a clickable duplicate of the rep RIGHT after the toggle card
        // so users can actually open the demo of the first version. The toggle
        // card itself only handles expand/collapse.
        out.push({ kind: "rep-clone", item: g.rep, groupKey: g.key, hasGroup: false });
        otherMembers.forEach(m => out.push({ kind: "member", item: m, groupKey: g.key, hasGroup: false }));
      }
    }
    return out;
  }, [groupMode, groups, sorted, expandedGroups, mode]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const formatDate = (d) => {
    if (!d) return "";
    const parts = d.split("-");
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (parts.length >= 2) return `${months[parseInt(parts[1]) - 1]} ${parts[0]}`;
    return d;
  };

  const toggleSelect = (url) => {
    setSelected(prev => { const next = new Set(prev); next.has(url) ? next.delete(url) : next.add(url); return next; });
  };

  const openShareModal = () => {
    const token = getToken();
    axios.get(`${API}/gallery/share-recipients`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setClients(res.data)).catch(() => toast.error("Failed to load users"));
    setShareModalOpen(true);
  };

  const handleShare = async () => {
    if (!selectedClient || selected.size === 0) return;
    setSharing(true);
    try {
      const token = getToken();
      await axios.post(`${API}/shared_ads`, { client_email: selectedClient, ad_urls: Array.from(selected) },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Shared ${selected.size} ads`);
      setShareModalOpen(false); setSelectMode(false); setSelected(new Set()); setSelectedClient(""); setGeneratedLink(null);
      fetchShares();
    } catch { toast.error("Failed to share"); }
    finally { setSharing(false); }
  };

  const handleGenerateLink = async () => {
    if (selected.size === 0) return;
    setGeneratingLink(true);
    try {
      const token = getToken();
      const res = await axios.post(`${API}/admin/shared-links`,
        { ad_urls: Array.from(selected), label: playlistName.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } });
      setGeneratedLink(`${window.location.origin}/gallery/${res.data.slug}`);
      toast.success("Link generated!");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to generate link"); }
    finally { setGeneratingLink(false); }
  };

  // CRUD handlers
  const openCreateForm = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setThumbFile(null);
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    setForm({
      brand: item.brand || item.title?.split(" - ")[0] || "",
      campaign: item.campaign || item.title?.split(" - ").slice(1).join(" - ") || "",
      version: item.version || "",
      demo_url: item.preview_url || "",
      date: item.date || "",
      categories: item.categories || [],
      formats: item.formats || [],
      interactions: item.interactions || [],
      thumbnail_url: item.thumbnail_url || "",
    });
    setThumbFile(null);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.brand || !form.demo_url) { toast.error("Brand and Demo Link are required"); return; }
    setSaving(true);
    try {
      const token = getToken();
      const fd = new FormData();
      fd.append("brand", form.brand);
      fd.append("campaign", form.campaign);
      fd.append("version", form.version || "");
      fd.append("demo_url", form.demo_url);
      fd.append("date", form.date);
      fd.append("categories", Array.isArray(form.categories) ? form.categories.join(",") : form.categories);
      fd.append("formats", Array.isArray(form.formats) ? form.formats.join(",") : form.formats);
      fd.append("interactions", Array.isArray(form.interactions) ? form.interactions.join(",") : form.interactions);
      fd.append("thumbnail_url", thumbFile ? "" : form.thumbnail_url);
      if (thumbFile) fd.append("thumbnail", thumbFile);

      if (editingItem) {
        await axios.put(`${API}/admin/showcase/${editingItem.id}`, fd, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Creative updated");
      } else {
        await axios.post(`${API}/admin/showcase`, fd, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Creative created");
      }
      setShowForm(false);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      const token = getToken();
      await axios.delete(`${API}/admin/showcase/${item.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Creative deleted");
      fetchItems();
    } catch { toast.error("Delete failed"); }
  };

  const sharedUrlSet = useMemo(() => {
    const s = new Set();
    allShares.forEach(share => (share.ad_urls || share.shared_urls || []).forEach(u => s.add(u)));
    return s;
  }, [allShares]);

  // Table view is only visible to Super Admin and Manager
  const userRole = mode === "admin" ? (localStorage.getItem("adminRole") || "") : "";
  const canSeeTableView = mode === "admin" && canEdit && ["super_admin", "manager"].includes(userRole);

  if (loading || loadingShared) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 text-[#ff3737] animate-spin" /></div>;

  return (
    <div className="space-y-5 w-full min-w-0 overflow-hidden" data-testid="ad-gallery">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#ebebeb] font-mono">Ad Gallery</h2>
          <p className="text-xs text-[#ebebeb]/40 font-mono mt-0.5">{sorted.length} of {sourceItems.length} creatives</p>
        </div>
        <div className="flex items-center gap-2">
          {mode === "admin" && canEdit && (
            <>
              <button onClick={openCreateForm}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-[#ff3737] text-white hover:bg-[#ff3737]/80 transition-colors"
                data-testid="add-creative-btn">
                <Plus className="w-3.5 h-3.5" /> Add Creative
              </button>
              <button onClick={() => { setSelectMode(!selectMode); if (selectMode) setSelected(new Set()); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono transition-colors ${
                  selectMode ? "bg-[#ff3737]/20 text-[#ff3737] border border-[#ff3737]/30" : "bg-white/5 text-[#ebebeb]/60 border border-white/10 hover:bg-white/10"
                }`} data-testid="share-mode-toggle">
                <Share2 className="w-3.5 h-3.5" /> {selectMode ? `${selected.size} selected` : "Share"}
              </button>
            </>
          )}
          {selectMode && selected.size > 0 && (
            <button onClick={openShareModal}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono bg-[#ff3737] text-white hover:bg-[#ff3737]/80 transition-colors"
              data-testid="share-selected-btn">
              <Users className="w-3.5 h-3.5" /> Share with Client
            </button>
          )}
          <button onClick={() => setViewMode("grid")} className={`p-1.5 rounded ${viewMode === "grid" ? "bg-white/10 text-white" : "text-[#ebebeb]/40 hover:text-white"}`} data-testid="view-grid"><LayoutGrid className="w-4 h-4" /></button>
          <button onClick={() => setViewMode("list")} className={`p-1.5 rounded ${viewMode === "list" ? "bg-white/10 text-white" : "text-[#ebebeb]/40 hover:text-white"}`} data-testid="view-list"><List className="w-4 h-4" /></button>
          {canSeeTableView && (
            <button onClick={() => setViewMode("table")} className={`p-1.5 rounded ${viewMode === "table" ? "bg-white/10 text-white" : "text-[#ebebeb]/40 hover:text-white"}`} data-testid="view-table" title="Inline edit table"><TableIcon className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ebebeb]/40" />
          <Input placeholder="Search by name..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-9 bg-white/5 border-white/10 text-[#ebebeb] placeholder:text-[#ebebeb]/30 font-mono text-xs h-8" data-testid="gallery-search" />
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Filter className="w-3.5 h-3.5 text-[#ebebeb]/30" />
          <FilterDropdown label="Category" options={options.categories} selected={category} onSelect={setCategory} onClear={() => setCategory(null)} />
          <FilterDropdown label="Format" options={options.formats} selected={format} onSelect={setFormat} onClear={() => setFormat(null)} />
          <FilterDropdown label="Interaction" options={options.interactions} selected={interaction} onSelect={setInteraction} onClear={() => setInteraction(null)} />
          {activeFilters > 0 && <button onClick={clearAll} className="text-[#ff3737] text-xs font-mono hover:underline" data-testid="clear-filters">Clear all</button>}
        </div>
        <div className="flex items-center gap-1.5 ml-auto">
          {viewMode !== "table" && (
            <div className="flex items-center bg-white/5 rounded-full p-0.5 border border-white/10" data-testid="gallery-group-toggle">
              <button
                onClick={() => setGroupMode("grouped")}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono transition-colors ${
                  groupMode === "grouped" ? "bg-[#ff3737]/20 text-[#ff3737]" : "text-[#ebebeb]/50 hover:text-[#ebebeb]/80"
                }`}
                data-testid="gallery-group-grouped"
                title="Collapse versions of the same campaign"
              >
                <Layers className="w-3 h-3" /> Grouped
              </button>
              <button
                onClick={() => setGroupMode("flat")}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-mono transition-colors ${
                  groupMode === "flat" ? "bg-[#ff3737]/20 text-[#ff3737]" : "text-[#ebebeb]/50 hover:text-[#ebebeb]/80"
                }`}
                data-testid="gallery-group-flat"
                title="Show every version individually"
              >
                <LayoutGrid className="w-3 h-3" /> Flat
              </button>
            </div>
          )}
          <ArrowUpDown className="w-3.5 h-3.5 text-[#ebebeb]/30" />
          <SortDropdown
            value={sortMode}
            onChange={(v) => { setSortMode(v); if (v === "shuffle") setShuffleSeed(Date.now()); }}
          />
          {sortMode === "shuffle" && (
            <button
              onClick={() => setShuffleSeed(Date.now())}
              className="flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-mono bg-white/5 text-[#ebebeb]/60 border border-white/10 hover:bg-white/10"
              data-testid="gallery-reshuffle"
              title="Reshuffle"
            >
              <Shuffle className="w-3 h-3" /> Re-roll
            </button>
          )}
        </div>
      </div>

      {/* Select All bar (visible only in share/select mode) */}
      {mode === "admin" && selectMode && sorted.length > 0 && (
        <div className="flex items-center justify-between px-3 py-2 rounded-lg bg-[#ff3737]/5 border border-[#ff3737]/20" data-testid="select-all-bar">
          <button
            onClick={() => {
              const allUrls = sorted.map((it) => it.preview_url).filter(Boolean);
              const allSelected = allUrls.every((u) => selected.has(u));
              setSelected((prev) => {
                const next = new Set(prev);
                if (allSelected) {
                  allUrls.forEach((u) => next.delete(u));
                } else {
                  allUrls.forEach((u) => next.add(u));
                }
                return next;
              });
            }}
            className="flex items-center gap-2 text-xs font-mono text-[#ebebeb]/80 hover:text-white"
            data-testid="select-all-btn"
          >
            {sorted.every((it) => selected.has(it.preview_url)) ? (
              <><CheckSquare className="w-3.5 h-3.5 text-[#ff3737]" /> Deselect all visible</>
            ) : (
              <><Square className="w-3.5 h-3.5 text-[#ebebeb]/40" /> Select all visible ({sorted.length})</>
            )}
          </button>
          <span className="text-xs font-mono text-[#ebebeb]/50" data-testid="selected-count">
            {selected.size} selected
          </span>
        </div>
      )}

      {/* Gallery */}
      {sorted.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-[#ebebeb]/40 font-mono text-sm">{mode === "client" ? "No ads have been shared with you yet." : "No creatives match your filters."}</p>
          {activeFilters > 0 && <button onClick={clearAll} className="text-[#ff3737] text-xs font-mono mt-2 hover:underline">Reset filters</button>}
        </div>
      ) : viewMode === "table" && canSeeTableView ? (
        <AdGalleryTable
          items={sorted}
          options={options}
          token={getToken()}
          onChange={fetchItems}
          requestDelete={(item) => handleDelete(item)}
        />
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {displayItems.map((entry, idx) => {
            const item = entry.item;
            if (!item) return null;
            const isSelected = selected.has(item.preview_url);
            const isShared = sharedUrlSet.has(item.preview_url);
            const showGroupBadge = entry.kind === "rep" && entry.hasGroup;
            const isExpanded = entry.expanded;
            const isMember = entry.kind === "member" || entry.kind === "rep-clone";
            const onCardClick = () => {
              if (selectMode) return toggleSelect(item.preview_url);
              if (showGroupBadge) return toggleGroup(entry.groupKey);
              window.open(item.preview_url, "_blank");
            };
            return (
              <div key={`${entry.kind}-${item.id || idx}`}
                className={`group relative bg-white/5 rounded-lg overflow-hidden border transition-all hover:scale-[1.02] cursor-pointer ${
                  isSelected ? "border-[#ff3737] ring-1 ring-[#ff3737]/50" : "border-white/5 hover:border-[#ff3737]/30"
                } ${isMember ? "ring-1 ring-[#ff3737]/15" : ""}`}
                onClick={onCardClick}
                data-testid={`gallery-item-${idx}`}>
                <div className="aspect-[2/1] overflow-hidden">
                  <img src={item.thumbnail_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-2.5">
                  <p className="text-white text-xs font-bold truncate">
                    {item.title}{item.version ? <span className="text-white/70"> · {item.version}</span> : null}
                  </p>
                  <p className="text-white/60 text-[10px] font-mono">{formatDate(item.date)}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(item.formats || []).slice(0, 2).map((f) => (
                      <span key={f} className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#ff3737]/20 text-[#ff3737] font-mono">{f}</span>
                    ))}
                    {(item.interactions || []).slice(0, 2).map((i) => (
                      <span key={i} className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/15 text-white/80 font-mono">{i}</span>
                    ))}
                  </div>
                </div>
                {showGroupBadge && (
                  <div
                    className="absolute bottom-2 left-2 flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-black/70 text-white text-[10px] font-mono backdrop-blur-sm"
                    data-testid={`group-badge-${idx}`}
                    title={isExpanded ? "Collapse versions" : "Expand all versions"}
                  >
                    <Layers className="w-2.5 h-2.5" />
                    {entry.memberCount} versions
                    {isExpanded ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                  </div>
                )}
                {selectMode && (
                  <div className={`absolute top-2 left-2 w-5 h-5 rounded flex items-center justify-center transition-colors ${isSelected ? "bg-[#ff3737]" : "bg-black/40 border border-white/30"}`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                )}
                {mode === "admin" && isShared && !selectMode && (
                  <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 text-[9px] font-mono">Shared</div>
                )}
                {/* Admin edit/delete */}
                {mode === "admin" && canEdit && !selectMode && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={(e) => { e.stopPropagation(); openEditForm(item); }}
                      className="p-1 rounded bg-black/60 text-white hover:bg-[#ff3737]/80" data-testid={`edit-creative-${idx}`}>
                      <Edit className="w-3 h-3" />
                    </button>
                    <button onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                      className="p-1 rounded bg-black/60 text-white hover:bg-red-600" data-testid={`delete-creative-${idx}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                )}
                {!selectMode && (mode !== "admin" || !canEdit) && !showGroupBadge && (
                  <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <ExternalLink className="w-3.5 h-3.5 text-white drop-shadow-lg" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-1.5">
          {displayItems.map((entry, idx) => {
            const item = entry.item;
            if (!item) return null;
            const isSelected = selected.has(item.preview_url);
            const showGroupBadge = entry.kind === "rep" && entry.hasGroup;
            const isMember = entry.kind === "member" || entry.kind === "rep-clone";
            const onRowClick = () => {
              if (selectMode) return toggleSelect(item.preview_url);
              if (showGroupBadge) return toggleGroup(entry.groupKey);
              window.open(item.preview_url, "_blank");
            };
            return (
              <div key={`${entry.kind}-${item.id || idx}`}
                onClick={onRowClick}
                className={`flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] hover:bg-white/5 border transition-all group cursor-pointer ${
                  isSelected ? "border-[#ff3737] bg-[#ff3737]/5" : "border-white/5 hover:border-[#ff3737]/20"
                } ${isMember ? "ml-8" : ""}`}
                data-testid={`gallery-list-item-${idx}`}>
                {selectMode && (
                  <div className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center ${isSelected ? "bg-[#ff3737]" : "bg-black/40 border border-white/30"}`}>
                    {isSelected && <Check className="w-3 h-3 text-white" />}
                  </div>
                )}
                <img src={item.thumbnail_url} alt={item.title} className="w-12 h-16 rounded object-cover flex-shrink-0" loading="lazy" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-[#ebebeb] text-sm font-medium truncate">{item.title}</p>
                    {item.version && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-[#ebebeb]/70 font-mono">{item.version}</span>}
                    {showGroupBadge && (
                      <span className="flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#ff3737]/20 text-[#ff3737] font-mono">
                        <Layers className="w-2.5 h-2.5" /> {entry.memberCount}
                        {entry.expanded ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-[#ebebeb]/40 text-xs font-mono">{formatDate(item.date)}</span>
                    {(item.categories || []).slice(0, 2).map((c) => (
                      <span key={c} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-[#ebebeb]/50 font-mono">{c}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {(item.formats || []).map((f) => (
                    <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#ff3737]/10 text-[#ff3737]/70 font-mono">{f}</span>
                  ))}
                  {(item.interactions || []).map((i) => (
                    <span key={i} className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/5 text-[#ebebeb]/60 font-mono">{i}</span>
                  ))}
                  {mode === "admin" && canEdit && !selectMode && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); openEditForm(item); }} className="p-1 rounded hover:bg-white/10 text-[#ebebeb]/40 hover:text-white"><Edit className="w-3.5 h-3.5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(item); }} className="p-1 rounded hover:bg-red-500/20 text-[#ebebeb]/40 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                  {!selectMode && (mode !== "admin" || !canEdit) && !showGroupBadge && <ExternalLink className="w-3.5 h-3.5 text-[#ebebeb]/20 group-hover:text-[#ff3737] transition-colors" />}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={(open) => { if (!open) setShowForm(false); }}>
        <DialogContent className="bg-[#191b1d] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-mono">{editingItem ? "Edit Creative" : "Add Creative"}</DialogTitle>
            <DialogDescription className="text-[#ebebeb]/50">Fill in the showcase creative details.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-2 max-h-[60vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Brand *</label>
                <Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="form-input text-sm" data-testid="form-brand" placeholder="e.g. Nike" />
              </div>
              <div>
                <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Campaign</label>
                <Input value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} className="form-input text-sm" data-testid="form-campaign" placeholder="e.g. Summer 2026" />
              </div>
            </div>
            <div>
              <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">
                Version <span className="text-[#ebebeb]/30">(optional — groups same brand+campaign together)</span>
              </label>
              <Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} className="form-input text-sm" data-testid="form-version" placeholder="e.g. v1, EN-US, Q1-Final" />
            </div>
            <div>
              <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Demo Link URL *</label>
              <Input value={form.demo_url} onChange={(e) => setForm({ ...form, demo_url: e.target.value })} className="form-input text-sm" data-testid="form-demo-url" placeholder="https://demo.example.com/preview.html" />
            </div>
            <div>
              <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Thumbnail</label>
              <div className="flex items-center gap-2">
                <Input value={form.thumbnail_url} onChange={(e) => { setForm({ ...form, thumbnail_url: e.target.value }); setThumbFile(null); }}
                  className="form-input text-sm flex-1" placeholder="Image URL or upload below" data-testid="form-thumb-url" />
                <label className="px-3 py-2 rounded-lg border border-white/10 text-[#ebebeb]/60 text-xs font-mono cursor-pointer hover:bg-white/5">
                  Upload
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => { setThumbFile(e.target.files?.[0] || null); if (e.target.files?.[0]) setForm({ ...form, thumbnail_url: "" }); }} data-testid="form-thumb-file" />
                </label>
              </div>
              {thumbFile && <p className="text-[#22c55e] text-xs font-mono mt-1">{thumbFile.name}</p>}
              {form.thumbnail_url && !thumbFile && (
                <img src={form.thumbnail_url} alt="Preview" className="mt-2 h-16 rounded object-cover" />
              )}
            </div>
            <div>
              <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Date (YYYY-MM)</label>
              <Input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="form-input text-sm" data-testid="form-date" placeholder="2026-04" />
            </div>
            <div>
              <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Category</label>
              <MultiSelectTag label="Category" options={options.categories} selected={form.categories} onChange={(v) => setForm({ ...form, categories: v })} placeholder="Select or add categories..." />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Format</label>
                <MultiSelectTag label="Format" options={options.formats} selected={form.formats} onChange={(v) => setForm({ ...form, formats: v })} placeholder="Select formats..." />
              </div>
              <div>
                <label className="text-[#ebebeb]/60 text-xs font-mono block mb-1">Interaction</label>
                <MultiSelectTag label="Interaction" options={options.interactions} selected={form.interactions} onChange={(v) => setForm({ ...form, interactions: v })} placeholder="Select interactions..." />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 rounded-lg border border-white/10 text-[#ebebeb]/60">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="ingenium-button" data-testid="save-creative-btn">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Saving...</> : editingItem ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      {mode === "admin" && canEdit && (
        <Dialog open={shareModalOpen} onOpenChange={(open) => { setShareModalOpen(open); if (!open) { setGeneratedLink(null); setPlaylistName(""); } }}>
          <DialogContent className="bg-[#1a1a1a] border border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[#ebebeb] font-mono">Share {selected.size} Ad{selected.size !== 1 ? "s" : ""}</DialogTitle>
              <DialogDescription className="text-[#ebebeb]/50 font-mono text-xs">Share with a user account or generate a named public playlist link.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div className="space-y-2">
                <label className="text-[#ebebeb]/70 text-xs font-mono">Share with User</label>
                <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#ebebeb] text-sm font-mono focus:outline-none focus:border-[#ff3737]/30" data-testid="share-client-select">
                  <option value="">Choose a user...</option>
                  {clients.map(c => (<option key={c.id} value={c.email}>{c.name} ({c.email})</option>))}
                </select>
                <button onClick={handleShare} disabled={sharing || !selectedClient}
                  className="w-full py-2.5 rounded-lg bg-[#ff3737] text-white font-mono text-sm hover:bg-[#ff3737]/80 disabled:opacity-50 transition-colors flex items-center justify-center gap-2" data-testid="confirm-share-btn">
                  <Users className="w-3.5 h-3.5" /> {sharing ? "Sharing..." : "Share with User"}
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10"></div></div>
                <div className="relative flex justify-center text-xs"><span className="bg-[#1a1a1a] px-3 text-[#ebebeb]/30">or</span></div>
              </div>
              <div className="space-y-2">
                <label className="text-[#ebebeb]/70 text-xs font-mono">Public Playlist Link</label>
                {!generatedLink && (
                  <Input
                    value={playlistName}
                    onChange={(e) => setPlaylistName(e.target.value)}
                    placeholder="Playlist name (optional)"
                    maxLength={120}
                    className="bg-white/5 border-white/10 text-[#ebebeb] text-sm font-mono placeholder:text-[#ebebeb]/30"
                    data-testid="playlist-name-input"
                  />
                )}
                {generatedLink ? (
                  <div className="flex items-center gap-2" data-testid="generated-link-container">
                    <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#ebebeb] text-xs font-mono truncate select-all">{generatedLink}</div>
                    <button onClick={() => { navigator.clipboard.writeText(generatedLink); toast.success("Copied"); }}
                      className="flex-shrink-0 p-2.5 rounded-lg bg-white/5 border border-white/10 text-[#ebebeb] hover:bg-white/10" data-testid="copy-link-btn"><Copy className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <button onClick={handleGenerateLink} disabled={generatingLink}
                    className="w-full py-2.5 rounded-lg bg-white/5 border border-white/10 text-[#ebebeb] font-mono text-sm hover:bg-white/10 transition-colors flex items-center justify-center gap-2" data-testid="generate-link-btn">
                    {generatingLink ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />}
                    {generatingLink ? "Generating..." : "Generate Public Link"}
                  </button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default AdGallery;


// ============================================================
// Table View — inline-edit ALL fields, Super Admin/Manager only
// Edits are staged locally and committed in one batch via "Save All".
// ============================================================
const AdGalleryTable = ({ items, options, token, onChange, requestDelete }) => {
  const [saving, setSaving] = useState(false);
  // Local snapshot of items so we can update rows in place without a parent refetch
  const [localItems, setLocalItems] = useState(items);
  // drafts: { [id]: { [field]: newValue } } — only changed fields
  const [drafts, setDrafts] = useState({});
  // Pending thumbnail uploads (File objects, one per id)
  const [pendingThumbs, setPendingThumbs] = useState({});
  // Object-URL previews so the new thumb shows immediately
  const [thumbPreviews, setThumbPreviews] = useState({});

  // Keep localItems in sync if parent passes a fresh list (e.g. after add/delete)
  useEffect(() => { setLocalItems(items); }, [items]);

  const COLS = [
    { key: "thumbnail_url", label: "Thumb",        w: "w-20" },
    { key: "brand",         label: "Brand",        w: "w-32" },
    { key: "campaign",      label: "Campaign",     w: "w-40" },
    { key: "version",       label: "Version",      w: "w-24" },
    { key: "date",          label: "Date (YYYY-MM)", w: "w-32" },
    { key: "preview_url",   label: "Demo URL",     w: "min-w-[200px]" },
    { key: "categories",    label: "Categories",   w: "min-w-[140px]" },
    { key: "formats",       label: "Formats",      w: "min-w-[140px]" },
    { key: "interactions",  label: "Interactions", w: "min-w-[140px]" },
  ];

  const getValue = (item, field) => {
    if (drafts[item.id]?.[field] !== undefined) return drafts[item.id][field];
    const v = item[field];
    if (Array.isArray(v)) return v.join(", ");
    return v ?? "";
  };

  const setDraft = (id, field, value) =>
    setDrafts((prev) => {
      const item = localItems.find((it) => it.id === id);
      const original = Array.isArray(item?.[field]) ? item[field].join(", ") : (item?.[field] ?? "");
      const next = { ...prev };
      if (value === original) {
        // User reverted to original — drop the draft
        if (next[id]) { const { [field]: _, ...rest } = next[id]; next[id] = rest; if (!Object.keys(next[id]).length) delete next[id]; }
      } else {
        next[id] = { ...(next[id] || {}), [field]: value };
      }
      return next;
    });

  const queueThumb = (id, file) => {
    if (!file) return;
    setPendingThumbs((prev) => ({ ...prev, [id]: file }));
    const url = URL.createObjectURL(file);
    setThumbPreviews((prev) => {
      const old = prev[id]; if (old) URL.revokeObjectURL(old);
      return { ...prev, [id]: url };
    });
  };

  const pendingCount =
    Object.keys(drafts).length + Object.keys(pendingThumbs).length;

  const discardAll = () => {
    // Revoke any in-memory blob previews
    Object.values(thumbPreviews).forEach((u) => URL.revokeObjectURL(u));
    setDrafts({});
    setPendingThumbs({});
    setThumbPreviews({});
  };

  // Build the FormData for one item from its draft + pending thumbnail
  const buildPatch = (id) => {
    const fd = new FormData();
    const d = drafts[id] || {};
    Object.entries(d).forEach(([field, value]) => {
      const backendField = field === "preview_url" ? "demo_url" : field;
      fd.append(backendField, value);
    });
    if (pendingThumbs[id]) fd.append("thumbnail", pendingThumbs[id]);
    return fd;
  };

  const saveAll = async () => {
    if (pendingCount === 0) return;
    setSaving(true);
    const idsToSave = new Set([...Object.keys(drafts), ...Object.keys(pendingThumbs)]);
    let ok = 0, fail = 0;
    const updatedItems = [...localItems];
    for (const id of idsToSave) {
      try {
        const resp = await axios.patch(`${API}/admin/showcase/${id}`, buildPatch(id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const idx = updatedItems.findIndex((it) => it.id === id);
        if (idx !== -1) updatedItems[idx] = { ...updatedItems[idx], ...resp.data };
        ok++;
      } catch (e) {
        fail++;
        toast.error(`Failed to save ${id.slice(0, 8)}…: ${e.response?.data?.detail || e.message}`);
      }
    }
    setLocalItems(updatedItems);
    if (ok > 0) toast.success(`Saved ${ok} ${ok === 1 ? "change" : "changes"}${fail ? ` (${fail} failed)` : ""}`);
    // Clear successful drafts. We keep failed ones so user can retry — for
    // simplicity here we clear everything; failed rows still show original values.
    Object.values(thumbPreviews).forEach((u) => URL.revokeObjectURL(u));
    setDrafts({});
    setPendingThumbs({});
    setThumbPreviews({});
    setSaving(false);
  };

  return (
    <div className="space-y-2">
      {/* Save-bar — only visible when there are pending changes */}
      {pendingCount > 0 && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-[#ff3737]/10 border border-[#ff3737]/30" data-testid="table-savebar">
          <span className="text-xs font-mono text-[#ff3737]" data-testid="table-pending-count">
            {pendingCount} unsaved {pendingCount === 1 ? "change" : "changes"}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={discardAll}
              disabled={saving}
              className="px-3 py-1.5 rounded text-xs font-mono text-[#ebebeb]/70 hover:text-white hover:bg-white/10"
              data-testid="table-discard-btn"
            >
              Discard
            </button>
            <button
              onClick={saveAll}
              disabled={saving}
              className="px-4 py-1.5 rounded text-xs font-mono bg-[#ff3737] text-white hover:bg-[#ff3737]/80 transition-colors disabled:opacity-50 flex items-center gap-1.5"
              data-testid="table-save-all-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Save All
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-white/10" data-testid="ad-gallery-table">
        <table className="w-full text-xs font-mono">
          <thead className="bg-white/5 sticky top-0 z-10">
            <tr>
              {COLS.map((c) => (
                <th key={c.key} className={`text-left px-2 py-2 text-[#ebebeb]/60 font-normal ${c.w}`}>{c.label}</th>
              ))}
              <th className="px-2 py-2 w-16 text-[#ebebeb]/60 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {localItems.length === 0 ? (
              <tr><td colSpan={COLS.length + 1} className="text-center text-[#ebebeb]/40 py-8">No creatives.</td></tr>
            ) : localItems.map((item) => {
              const rowDirty = !!drafts[item.id] || !!pendingThumbs[item.id];
              return (
                <tr
                  key={item.id}
                  className={`border-t border-white/5 hover:bg-white/[0.02] ${rowDirty ? "bg-[#ff3737]/[0.03]" : ""}`}
                  data-testid={`table-row-${item.id}`}
                >
                  {/* Thumbnail */}
                  <td className="px-2 py-2">
                    <label className="block w-16 h-12 rounded overflow-hidden cursor-pointer relative group" title="Click to replace">
                      <img src={thumbPreviews[item.id] || item.thumbnail_url} alt="" className="w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Edit className="w-3 h-3 text-white" />
                      </div>
                      {pendingThumbs[item.id] && (
                        <span className="absolute top-0 right-0 bg-[#ff3737] text-white text-[8px] px-1 leading-tight rounded-bl">●</span>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => queueThumb(item.id, e.target.files?.[0])}
                        data-testid={`table-thumb-${item.id}`}
                      />
                    </label>
                  </td>
                  {/* Text fields */}
                  {COLS.slice(1).map((c) => {
                    // Date stored as "YYYY-MM" — use <input type="month"> to render & accept that format
                    const isDate = c.key === "date";
                    return (
                      <td key={c.key} className="px-2 py-2">
                        <input
                          type={isDate ? "month" : "text"}
                          value={getValue(item, c.key)}
                          onChange={(e) => setDraft(item.id, c.key, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                          className={`w-full bg-transparent border rounded px-1.5 py-1 outline-none text-[#ebebeb] transition-colors ${
                            drafts[item.id]?.[c.key] !== undefined
                              ? "border-[#ff3737]/40 bg-[#ff3737]/5"
                              : "border-transparent hover:border-white/10 focus:border-[#ff3737]/50 focus:bg-white/5"
                          }`}
                          data-testid={`table-${c.key}-${item.id}`}
                        />
                      </td>
                    );
                  })}
                  <td className="px-2 py-2 text-right">
                    <button
                      onClick={() => requestDelete(item)}
                      className="p-1 rounded hover:bg-red-500/20 text-[#ebebeb]/40 hover:text-red-400"
                      data-testid={`table-delete-${item.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="px-3 py-2 text-[10px] text-[#ebebeb]/40 bg-white/[0.02]" data-testid="table-help">
          Edit cells freely — changes are staged locally. Click <span className="text-[#ff3737]">Save All</span> at the top to commit. Date is YYYY-MM. Comma-separated values for Categories/Formats/Interactions.
        </p>
      </div>
    </div>
  );
};
